/**
 * GTM Event Queue
 *
 * Singleton that guarantees page_view fires before any other event.
 * Events pushed before page_view is sent are queued and flushed once
 * page_view fires. A safety timeout prevents infinite queuing.
 */

import { devLog, devWarn } from '@/lib/utils/devLog';
import type { GTMEvent } from './types';

const MAX_QUEUE_SIZE = 50;
const SAFETY_TIMEOUT_MS = 10_000;

interface QueueState {
  initialized: boolean;
  queue: GTMEvent[];
  timeoutId: ReturnType<typeof setTimeout> | null;
}

const state: QueueState = {
  initialized: false,
  queue: [],
  timeoutId: null,
};

function pushToDataLayer(event: GTMEvent): void {
  if (globalThis.window === undefined) return;

  globalThis.dataLayer = globalThis.dataLayer || [];
  globalThis.dataLayer.push(event);

  if (process.env.NEXT_PUBLIC_GA_DEBUG_MODE === 'true') {
    devLog('[GTM]', event.event, event);
  }
}

function flushQueue(): void {
  const events = [...state.queue];
  state.queue = [];

  for (const event of events) {
    pushToDataLayer(event);
  }

  if (events.length > 0) {
    devLog(`[GTM] Flushed ${events.length} queued event(s)`);
  }
}

function clearSafetyTimeout(): void {
  if (state.timeoutId !== null) {
    clearTimeout(state.timeoutId);
    state.timeoutId = null;
  }
}

function startSafetyTimeout(): void {
  clearSafetyTimeout();
  state.timeoutId = setTimeout(() => {
    if (!state.initialized) {
      devWarn('[GTM] Safety timeout: flushing queue without page_view');
      state.initialized = true;
      flushQueue();
    }
  }, SAFETY_TIMEOUT_MS);
}

/**
 * Push an event to the dataLayer. If page_view hasn't fired yet and
 * `requiresInit` is true, the event is queued until page_view fires.
 */
export function pushEvent(event: GTMEvent, requiresInit = true): void {
  if (globalThis.window === undefined) return;

  if (event.event === 'page_view') {
    state.initialized = true;
    clearSafetyTimeout();
    pushToDataLayer(event);
    flushQueue();
    return;
  }

  if (!state.initialized && requiresInit) {
    if (state.queue.length >= MAX_QUEUE_SIZE) {
      devWarn('[GTM] Queue full, dropping event:', event.event);
      return;
    }

    state.queue.push(event);

    if (state.timeoutId === null) {
      startSafetyTimeout();
    }
    return;
  }

  pushToDataLayer(event);
}

/**
 * Reset the queue state on route change. Called by GoogleAnalyticsProvider
 * before each new page_view.
 */
export function resetQueue(): void {
  state.initialized = false;
  state.queue = [];
  clearSafetyTimeout();
}

/**
 * Get current queue state for debugging.
 */
export function getQueueState(): {
  initialized: boolean;
  queueLength: number;
  hasTimeout: boolean;
} {
  return {
    initialized: state.initialized,
    queueLength: state.queue.length,
    hasTimeout: state.timeoutId !== null,
  };
}
